import React, { useContext, useMemo, useState, useEffect, useRef } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
import PropTypes from 'prop-types';
import {
  Grid,
  FormControl,
  Select,
  TextField,
  Icon,
  Icons,
  styled,
} from '@nextiva/next-ui-components';
import { drawerSchema, requiredSchema } from 'utils/phone-number';
import { CallForwardContext, actionTypes } from 'containers/CallForwarding';
import Footer from 'components/Drawer/Footer';
import { PhoneNumContainer } from 'components/CallForwardSelective/CallForwardSelective.styles';
import {
  AddNumber,
  PhoneNumberTextField,
  PhoneFormControl,
  FormControlComponent,
  ErrorHelperText,
  Item,
  Label,
  RemoveButton,
  RadioGrp,
  RadioBtn,
  CheckboxComponent,
  FormLabel,
} from './SelectiveDrawerForm.styles';

const FORWARD_TO_SPECIFIED_NUMBER = 'Forward To Specified Number';

const StyledIcon = styled(Icon)`
  color: ${({ theme }) => theme.palette.common.black};
`;

const conditionValList = (conditionList, drawerType, conditionIndex) => {
  const list = [...conditionList];
  if (drawerType === 'Update') {
    list.splice(conditionIndex, 1);
    return list;
  }

  return list;
};

const SelectiveDrawerForm = ({
  appendCondition,
  conditionList,
  updateCondition,
}) => {
  const { state, dispatch } = useContext(CallForwardContext);
  const {
    originalFields: {
      selective: { availableSchedules },
    },
  } = state;

  const schedules = useRef([
    ...availableSchedules,
    {
      scheduleName: 'Every Day All Day',
      scheduleLevel: 'ACCOUNT',
    },
  ]);

  const initialFormValues = useMemo(() => {
    const index = state.drawer.updateIndex;
    const notNull = index !== null;

    const schedule = () => {
      return schedules.current.find(
        (s) => s.scheduleName === conditionList[index].schedule.scheduleName,
      );
    };

    return {
      name: notNull ? conditionList[index].name : '', // "Description" text field
      active: notNull ? conditionList[index].active : true, // this is set via the CallForwardSelectiveTable's actions button
      schedule: notNull ? schedule() : '',
      callFrom: notNull ? conditionList[index].callFrom : [], // "Phone Numbers" dynamic text fields: array of objects { extension: string, phoneNumber: string }
      blacklisted: notNull ? conditionList[index].blacklisted : false, // this key has no representation in the interface
      forwardTo: notNull ? conditionList[index].forwardTo : '', // "Specified Phone Number" text field
      callsToType: notNull ? conditionList[index].callsToType : '', // this key has no representation in the interface
      callsToNumber: notNull ? conditionList[index].callsToNumber : '', // this key has no representation in the interface
      callsToExtension: notNull ? conditionList[index].callsToExtension : '', // this key has no representation in the interface
      forwardOption: notNull ? conditionList[index].forwardOption : '', // "Forward To" select drop down - this the forwardTo phone number
      forwardFromAnyPhoneNumber: notNull
        ? conditionList[index].forwardFromAnyPhoneNumber.toString()
        : 'true', // "From any/all phone numbers" radio button
      forwardFromAnyPrivateNumber: notNull
        ? conditionList[index].forwardFromAnyPrivateNumber
        : false, // "Any private number" checkbox
      forwardFromAnyUnavailableNumber: notNull
        ? conditionList[index].forwardFromAnyUnavailableNumber
        : false, // "Any unavailable number" checkbox
    };
  }, [schedules, conditionList, state.drawer.updateIndex]);

  const [disabled, setDisabled] = useState(true);

  const validationList = useRef(
    conditionValList(
      conditionList,
      state.drawer.type,
      state.drawer.updateIndex,
    ),
  );

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    setValue,
    formState,
    clearErrors,
    watch,
  } = useForm({
    defaultValues: initialFormValues,
    mode: 'onSubmit',
    resolver: yupResolver(drawerSchema(validationList.current)),
  });

  const forwardFromAnyPhoneNumberVal = watch('forwardFromAnyPhoneNumber');
  const forwardOptionVal = watch('forwardOption');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'callFrom',
  });

  useEffect(() => {
    if (Object.keys(formState.dirtyFields).length > 0) {
      const values = getValues();

      requiredSchema(
        forwardOptionVal === FORWARD_TO_SPECIFIED_NUMBER,
        conditionList,
      )
        .isValid(values)
        .then((valid) => {
          setDisabled(!valid);
        });
    }
  }, [formState, forwardOptionVal, getValues, conditionList]);

  const onChangeRadioGroup = (e) => {
    if (e.target.value === 'true') {
      setValue('forwardFromAnyPrivateNumber', false);
      setValue('forwardFromAnyUnavailableNumber', false);
      setValue('callFrom', []);
      clearErrors('forwardFromAnyPhoneNumber');
    }
  };

  const onAddSpecificPhoneNumberClick = () => {
    append({ phoneNumber: '', extension: null });
    clearErrors('forwardFromAnyPhoneNumber');
  };

  const onRemoveSpecificPhoneNumberClick = (index) => {
    remove(index);
    clearErrors('forwardFromAnyPhoneNumber');
  };

  const closeDrawer = () => {
    // reset to initial state values
    setDisabled(true);
    reset();

    dispatch({
      type: actionTypes.TOGGLE_DRAWER,
      payload: {
        open: false,
        type: '',
        updateIndex: null,
      },
    });
  };

  const onSubmit = (data) => {
    // Note: Must recast the forwardFromAnyPhoneNumber from a string to a boolean
    // because its value is driven by a radio group which only returns string values.
    data.forwardFromAnyPhoneNumber = data.forwardFromAnyPhoneNumber === 'true';

    if (state.drawer.type === 'Add') {
      appendCondition(data);
    } else {
      updateCondition(state.drawer.updateIndex, data);
    }

    closeDrawer();
  };

  return (
    <form
      style={{ marginBottom: '80px' }}
      onSubmit={handleSubmit(onSubmit)}
      data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-Form"
    >
      <Grid container>
        <Grid className="field-container" item xs={12}>
          <Controller
            control={control}
            defaultValue={initialFormValues.name}
            name="name"
            render={({ field, fieldState }) => (
              <TextField
                style={{ marginBottom: '16px' }}
                fullWidth
                id="description"
                label="DESCRIPTION"
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                {...field}
                error={Boolean(fieldState.error?.type)}
                helperText={fieldState.error?.message}
                data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-Description"
              />
            )}
          />
        </Grid>
        <Grid className="field-container" item xs={12}>
          <FormControlComponent variant="outlined" fullWidth>
            <Label
              id="forward-option"
              data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-ForwardToLabel"
            >
              FORWARD TO
            </Label>
            <Controller
              control={control}
              defaultValue={initialFormValues.forwardTo}
              name="forwardOption"
              render={({ field }) => (
                <Select
                  id="forward-option"
                  {...field}
                  MenuProps={{
                    getContentAnchorEl: null,
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'center',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'center',
                    },
                  }}
                >
                  <Item
                    value="Forward To Default Number"
                    disableRipple
                    data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-ForwardToMenu-ForwardToDefaultNumber"
                  >
                    Forward To Default Number
                  </Item>
                  <Item
                    value="Forward To Specified Number"
                    disableRipple
                    data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-ForwardToMenu-ForwardToSpecifiedNumber"
                  >
                    Forward To Specified Number
                  </Item>
                  <Item
                    value="Do not forward"
                    disableRipple
                    data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-ForwardToMenu-DoNotForward"
                  >
                    Do not forward
                  </Item>
                </Select>
              )}
            />
          </FormControlComponent>
        </Grid>
        {forwardOptionVal === FORWARD_TO_SPECIFIED_NUMBER && (
          <Grid className="field-container" item xs={12}>
            <Controller
              control={control}
              defaultValue={initialFormValues.callsToNumber}
              name="forwardTo"
              render={({ field, fieldState }) => {
                return (
                  <TextField
                    style={{ marginBottom: '16px' }}
                    fullWidth
                    id="specified-phone-number"
                    label="SPECIFIED PHONE NUMBER"
                    InputProps={{ disableUnderline: true }}
                    InputLabelProps={{ shrink: true }}
                    {...field}
                    error={Boolean(fieldState.error?.type)}
                    helperText={fieldState.error?.message}
                    data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-SpecifiedPhoneNumber"
                  />
                );
              }}
            />
          </Grid>
        )}
        <Grid className="field-container" item xs={12}>
          <FormControlComponent variant="outlined" fullWidth>
            <Label
              id="schedule"
              data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-ScheduleLabel"
            >
              SCHEDULE
            </Label>
            <Controller
              control={control}
              defaultValue=""
              name="schedule"
              render={({ field }) => (
                <Select
                  id="schedule"
                  labelId="schedule"
                  MenuProps={{
                    getContentAnchorEl: null,
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'center',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'center',
                    },
                  }}
                  {...field}
                >
                  {schedules.current?.map((schedule) => {
                    return (
                      <Item
                        value={schedule}
                        key={schedule.scheduleName}
                        disableRipple
                        data-testid={`PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-ScheduleMenu-${schedule.scheduleName}`}
                      >
                        {schedule.scheduleName}
                      </Item>
                    );
                  })}
                </Select>
              )}
            />
          </FormControlComponent>
        </Grid>
      </Grid>
      <Grid className="field-container" item xs={12}>
        <FormControl component="fieldset">
          <Controller
            control={control}
            defaultValue={initialFormValues.forwardFromAnyPhoneNumber}
            name="forwardFromAnyPhoneNumber"
            render={({ field, fieldState }) => {
              return (
                <RadioGrp
                  aria-label="any or specific phone numbers"
                  {...field}
                  onChange={(event) => {
                    field.onChange(event);
                    onChangeRadioGroup(event);
                  }}
                  data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-RadioGroup"
                >
                  <FormLabel
                    value="true"
                    control={
                      <RadioBtn
                        disableRipple
                        data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-FromAllPhoneNumbersRadioButton"
                      />
                    }
                    label="From any/all phone numbers"
                    data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-FromAllPhoneNumbersLabel"
                  />
                  <FormLabel
                    value="false"
                    control={
                      <RadioBtn
                        disableRipple
                        data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-FromSpecificPhoneNumbersRadioButton"
                      />
                    }
                    label="From specific phone numbers"
                    data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-FromSpecificPhoneNumbersLabel"
                  />
                  <ErrorHelperText>{fieldState.error?.message}</ErrorHelperText>
                </RadioGrp>
              );
            }}
          />
        </FormControl>
      </Grid>
      {forwardFromAnyPhoneNumberVal === 'false' && (
        <>
          <Grid className="field-container" item xs={12}>
            <FormControl component="fieldset">
              <Controller
                name="forwardFromAnyPrivateNumber"
                control={control}
                defaultValue={initialFormValues.forwardFromAnyPrivateNumber}
                render={({ field }) => {
                  return (
                    <FormLabel
                      control={
                        <CheckboxComponent
                          {...field}
                          checked={field.value}
                          onChange={(event) => {
                            field.onChange(event);
                            clearErrors('forwardFromAnyPhoneNumber');
                          }}
                          disableRipple
                          data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-ForwardFromAnyPrivateNumberCheckBox"
                        />
                      }
                      label="Any private number"
                      data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-ForwardFromAnyPrivateNumberLabel"
                    />
                  );
                }}
              />
            </FormControl>
          </Grid>
          <Grid className="field-container" item xs={12}>
            <PhoneFormControl component="fieldset">
              <Controller
                name="forwardFromAnyUnavailableNumber"
                control={control}
                defaultValue={initialFormValues.forwardFromAnyUnavailableNumber}
                render={({ field }) => {
                  return (
                    <FormLabel
                      control={
                        <CheckboxComponent
                          {...field}
                          checked={field.value}
                          onChange={(event) => {
                            field.onChange(event);
                            clearErrors('forwardFromAnyPhoneNumber');
                          }}
                          disableRipple
                          data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-ForwardFromAnyUnavailableNumberCheckBox"
                        />
                      }
                      label="Any unavailable number"
                      data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-ForwardFromAnyUnavailableNumberLabel"
                    />
                  );
                }}
              />
            </PhoneFormControl>
          </Grid>
          {fields.map((callFrom, index) => (
            <PhoneNumContainer
              className="field-container"
              item
              xs={12}
              key={callFrom.id}
            >
              <Controller
                control={control}
                defaultValue={callFrom.phoneNumber}
                name={`callFrom.${index}.phoneNumber`}
                render={({ field, fieldState }) => (
                  <>
                    <PhoneNumberTextField
                      id={`phone-number-${callFrom.id}`}
                      label="PHONE NUMBER"
                      InputProps={{ disableUnderline: true }}
                      InputLabelProps={{ shrink: true }}
                      {...field}
                      error={Boolean(fieldState.error?.type)}
                      helperText={fieldState.error?.message}
                      data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-PhoneNumber"
                    />
                    <RemoveButton
                      onClick={() => onRemoveSpecificPhoneNumberClick(index)}
                      variant="text"
                      error={fieldState.error}
                      data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-RemoveButton"
                    >
                      <StyledIcon icon={Icons.remove} size={18} />
                      <span
                        style={{ color: 'red', marginLeft: 4 }}
                        data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-RemoveButtonText"
                      >
                        Remove
                      </span>
                    </RemoveButton>
                  </>
                )}
              />
            </PhoneNumContainer>
          ))}
          {fields.length < 12 && (
            <Grid item xs={12}>
              <AddNumber
                iconPosition="left"
                onClick={onAddSpecificPhoneNumberClick}
                variant="text"
                data-testid="PlatformAdminUI-TeamVoiceSettings-SelectiveDrawerForm-AddPhoneNumberButton"
              >
                <StyledIcon icon={Icons.addCircle} size={18} />
                <span style={{ color: '#005FEC', marginLeft: 4 }}>
                  Add phone number
                </span>
              </AddNumber>
            </Grid>
          )}
        </>
      )}
      <Footer
        saveText={state.drawer.type}
        saveEnabled={!disabled}
        cancelOnClick={closeDrawer}
        saveOnClick={handleSubmit(onSubmit)}
      />
    </form>
  );
};

SelectiveDrawerForm.propTypes = {
  appendCondition: PropTypes.func,
  conditionList: PropTypes.array,
  updateCondition: PropTypes.func,
};

export default SelectiveDrawerForm;
